import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { changeTab, history } from '../../../utils';
import type { ChangeEvent } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import Stack from '@mui/material/Stack';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import GeneralUserInfoBanner from './assets/GeneralUserInfoBanner';
import { ISystem } from '../../../ts/interface/system.interface';
import UserMemberships from './userMemberships';
import UserCoupons from './userCoupons';
import { UserInterface } from '../../../ts/interface/user.interface';
import UserProblems from './userProblems';
import { IProblem } from '../../../ts/interface/problem.interface';
import UserRfidCards from './userRfidCards';
import UserRentals from './userRentals';
import UserTransactions from '../userTransactions';
import {
  IUserDetail,
  IUserOrganizationDetails,
  UserDetailStatus,
} from '../../../ts/interface/userDetail.interface';
import { useParams } from 'react-router-dom';
import { useSystemStore } from '../../../store/systemStore';
import PageContent from '../../../components/Redesing/page-content';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import { authorizeUser, banUser } from '../../../api/users';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import useDirectusData from '../../../hooks/directus/query/useDirectusData';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import { DirectusData } from '../../../ts/interface/directus';
import PersonIcon from '@mui/icons-material/Person';
import {
  useUserRentalsQuery,
  useUserTransactionsQuery,
  mapRelayRentalsToIRentals,
  mapRelayTransactionsToUnified,
} from '../../../hooks/users/query/V2';
import type { IRental } from '../../../ts/interface/rental.interface';
import type { UnifiedTransaction } from '../../../hooks/users/query/V2';

interface GeneralUserInfoProps {
  isLoading: boolean;
  onSuccess: Function;
  privateSystem: boolean;
  system: ISystem | ISystem[];
  user: UserInterface;
  userDetails: IUserDetail | IUserOrganizationDetails;
  usesWallet: boolean;
  problems: IProblem[];
  unitsTitle: string;
  systemGid: string;
  creditCards?: any[];
  userDelinquent?: boolean;
  organizationId?: string;
  isOrgContext?: boolean;
}

const tabsList = [
  { label: 'Details', value: 'general' },
  { label: 'Rentals', value: 'rentals' },
  { label: 'Transactions', value: 'transactions' },
  { label: 'Coupons', value: 'coupons' },
];
enum UserStatusText {
  BAN = 'Ban',
  AUTHORIZE = 'Authorize',
}

const GeneralUserInfo = ({
  privateSystem,
  onSuccess,
  user,
  userDetails,
  system,
  problems,
  systemGid,
  creditCards,
  userDelinquent,
  organizationId,
  isOrgContext,
}: GeneralUserInfoProps) => {
  const { userPhone, currentTab: currentActiveTab = 'general' } = useParams<{
    userPhone: string;
    currentTab: string;
  }>();
  const [userStatusText, setUserStatusText] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [currentTab, setCurrentTab] = useState<string>('general');
  const systemOldId = useSystemStore((state) => state.systemInfo.oldId);
  const [rentalsSearch, setRentalsSearch] = useState('');
  const debouncedRentalsSearch = useSearchDebounce(rentalsSearch);
  const [transactionsSearch, setTransactionsSearch] = useState('');
  const debouncedTransactionsSearch = useSearchDebounce(transactionsSearch);

  const { data: directus } = useDirectusData(
    'adminSiteSpace',
    { code: 'user-details' },
    true
  ) as { data: { value: DirectusData } | undefined };
  const directusData = directus?.value;

  useEffect(() => {
    if (currentActiveTab !== currentTab) {
      setCurrentTab(currentActiveTab);
    }
  }, [currentActiveTab, currentTab]);

  useEffect(() => {
    if (userDetails) {
      setUserStatusText(
        userDetails.banned || userDetails.status === UserDetailStatus.BLACKLISTED
          ? UserStatusText.AUTHORIZE
          : UserStatusText.BAN
      );
    }
  }, [userDetails]);

  // For ban/authorize in org context we need the system's old_id
  // (the REST endpoint identifies the system by old_id header).
  const actionSystemOldId = useMemo<string | undefined>(() => {
    if (!isOrgContext) return undefined;
    const orgDetails = userDetails as IUserOrganizationDetails;
    return orgDetails.systems?.[0]?.old_id || undefined;
  }, [isOrgContext, userDetails]);

  // Lazy-load rentals only when tab is active; search is filtered server-side
  const {
    data: rentalsData,
    isLoading: rentalsLoading,
    isFetching: rentalsFetching,
  } = useUserRentalsQuery(userPhone, {
    enabled: currentTab === 'rentals',
    systemId: isOrgContext ? undefined : systemGid,
    organizationId: isOrgContext ? organizationId : undefined,
    search: debouncedRentalsSearch || undefined,
  });

  const userRentals = useMemo<Partial<IRental>[]>(() => {
    if (!rentalsData) return [];
    return mapRelayRentalsToIRentals(rentalsData);
  }, [rentalsData]);

  // Lazy-load transactions only when tab is active; search is filtered server-side
  const {
    data: transactionsData,
    isLoading: transactionsLoading,
    isFetching: transactionsFetching,
  } = useUserTransactionsQuery(userPhone, {
    enabled: currentTab === 'transactions',
    systemId: isOrgContext ? undefined : systemGid,
    organizationId: isOrgContext ? organizationId : undefined,
    search: debouncedTransactionsSearch || undefined,
  });

  const userTransactions = useMemo<UnifiedTransaction[]>(() => {
    if (!transactionsData) return [];
    return mapRelayTransactionsToUnified(
      transactionsData.walletTransactions,
      transactionsData.charges
    );
  }, [transactionsData]);

  const handleTabsChange = useCallback(
    (event: ChangeEvent<any>, value: string): void => {
      changeTab(`${userPhone}/${value}`, currentTab, 'users');
    },
    [currentTab, userPhone]
  );

  return (
    <>
      <PageContent
        tabsList={tabsList}
        backLink={'/users'}
        detailIcon={<PersonIcon />}
        contentTitle={'Users'}
        itemName={userDetails.fullName}
        itemChipValue={userDetails.id}
        chipLabel={'User ID'}
        currentTabV={currentTab}
        handleTabsChange={handleTabsChange}
        tabsChildren={
          <>
            {currentTab === 'general' && (
              <div>
                <Grid container spacing={2}>
                  <Grid xs={12} md={5} lg={5} xl={5}>
                    <Stack spacing={2}>
                      <GeneralUserInfoBanner
                        paymentInfoDocumentationLink={
                          directusData?.detailed_views?.find(
                            (item) => item.identifier === 'payment-info'
                          )?.documentation_link
                        }
                        privateSystem={privateSystem}
                        userDetails={userDetails}
                        system={system}
                        onSuccess={onSuccess}
                        creditCards={creditCards}
                        userDelinquent={userDelinquent}
                      />
                    </Stack>
                  </Grid>
                  <Grid xs={12} md={7} lg={7} xl={7}>
                    <Stack spacing={2}>
                      <UserMemberships
                        documentationLink={
                          directusData?.detailed_views?.find(
                            (item) => item.identifier === 'user-memberships'
                          )?.documentation_link
                        }
                        user={user}
                        onRefresh={onSuccess}
                      />
                      <UserProblems
                        documentationLink={
                          directusData?.detailed_views?.find(
                            (item) => item.identifier === 'reported-problems'
                          )?.documentation_link
                        }
                        problems={problems}
                      />
                      <UserRfidCards
                        documentationLink={
                          directusData?.detailed_views?.find(
                            (item) => item.identifier === 'user-rfid-cards'
                          )?.documentation_link
                        }
                        rfidCards={user?.rfidCards || []}
                        userPhone={userDetails.phone}
                        onRefresh={onSuccess}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </div>
            )}
            {currentTab === 'rentals' && (
              <div>
                <UserRentals
                  isLoading={rentalsLoading}
                  isFetching={rentalsFetching}
                  rentals={userRentals}
                  searchText={rentalsSearch}
                  onSearchChange={setRentalsSearch}
                  userPhone={userDetails.phone}
                  noRentalsText={
                    'This user does not have any rentals matching the criteria'
                  }
                  onAddRental={() =>
                    history.push(`/users/${userDetails.phone}/rentals/add`)
                  }
                />
              </div>
            )}
            {currentTab === 'transactions' && (
              <div>
                <UserTransactions
                  isLoading={transactionsLoading}
                  isFetching={transactionsFetching}
                  transactions={userTransactions}
                  searchText={transactionsSearch}
                  onSearchChange={setTransactionsSearch}
                />
              </div>
            )}
            {currentTab === 'coupons' && (
              <div>
                <UserCoupons
                  userOldId={userDetails.phone}
                  userId={userDetails.id}
                  systemOldId={isOrgContext ? undefined : systemOldId}
                  documentationLink={
                    directusData?.detailed_views?.find(
                      (item) => item.identifier === 'user-coupons'
                    )?.documentation_link
                  }
                />
              </div>
            )}
            {showConfirmationModal && (
              <MovaticCustomModal
                useContentDividers
                open={showConfirmationModal}
                onClose={() => setShowConfirmationModal(false)}
                title={`${userStatusText} User!`}
                content={
                  <div>
                    {`Are you sure you would like to ${userStatusText} ${userDetails?.fullName}?`}
                  </div>
                }
                action={
                  <IconButtonMenu
                    buttons={[
                      {
                        label: 'Cancel',
                        onClick: () => setShowConfirmationModal(false),
                        startIcon: <CloseIcon />,
                      },
                      {
                        label: 'Submit',
                        onClick: () => {
                          if (userStatusText === UserStatusText.BAN) {
                            CustomAsyncToast({
                              promise: () =>
                                banUser(userDetails.phone, actionSystemOldId),
                              successMessage: () => 'User banned successfully',
                              loadingMessage: 'Banning user...',
                              errorMessage: 'Error banning user',
                            }).then((r) => {
                              if (r) {
                                setShowConfirmationModal(false);
                                onSuccess();
                              }
                            });
                          } else {
                            CustomAsyncToast({
                              promise: () =>
                                authorizeUser(userDetails.phone, actionSystemOldId),
                              successMessage: () => 'User authorized successfully',
                              loadingMessage: 'Authorizing user...',
                              errorMessage: 'Error authorizing user',
                            }).then((r) => {
                              if (r) {
                                setShowConfirmationModal(false);
                                onSuccess();
                              }
                            });
                          }
                        },
                        startIcon: <CheckCircleIcon />,
                      },
                    ]}
                  />
                }
              />
            )}
          </>
        }
        itemTopButtons={
          <>
            <IconButtonMenu
              buttons={[
                {
                  dataId: 'user-status',
                  label: userStatusText,
                  onClick: () => setShowConfirmationModal(true),
                  startIcon:
                    userStatusText === 'Ban' ? <BlockIcon /> : <CheckCircleIcon />,
                },
                {
                  label: 'Edit',
                  disabled: !!userDetails.emailAddress || !userDetails.autoGenNumber,
                  onClick: () => history.push(`/users/${userDetails.phone}/edit`),
                  startIcon: <EditIcon />,
                },
              ]}
            />
          </>
        }
      />
    </>
  );
};

export default GeneralUserInfo;
